















































































import { Component, Vue } from "vue-property-decorator";
import { latLng, LatLng, LatLngBounds, Marker, Map as LMap } from "leaflet";
import CollectionEventPopup from "@/components/CollectionEvents/CollectionEventPopup.vue";
import MapFiltersContainer from "@/components/Map/MapFiltersContainer.vue";
import MapAddressSearch from "@/components/Map/MapAddressSearch.vue";
import LoadingPanel from "@/components/LoadingPanel.vue";
import mapStore from "@/store/mapStore";
import { Observer } from "mobx-vue";
import { reaction, toJS } from "mobx";
import { CollectionEventSummaryModel, CollectionIndexModel } from "@/api/CollectionEvents";
import TruckMarkers from "./TruckMarkers.vue";
import VideoRequestMarkers from "./VideoRequestMarkers.vue";
import { RequestModel, getStatus, unsuccessfulTaskCount, isTaskUnsuccessful } from "@/api/Requests";
import forOwn from "lodash/forOwn";
import eventsHub from "@/hubs/EventsHub";
import { BinEvent } from "@/hubs/events";
import userStore from "@/store/userStore";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import { DATE_FORMAT } from "@/Constants";
import FormattedAddress from "@/components/Address/FormattedAddress.vue";
import { addDataCyToCollectionEventIcons } from "@/UITestHelper/UITestHelper";
import { requestMarker } from "@/assets/icons/RequestMarkers";
import { LoadingStore } from "@/store/loadingStore";
import RequestPopup from "./RequestPopup.vue";
import { MapProvider } from "@/api/CouncilConfiguration";

const L = window["L"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(L.TileLayer as any).PropertyBoundaries = L.TileLayer.extend({
  getTileUrl: function(coords: { x: number; y: number; z: number }) {
    const tileUrl = mapStore.propertyBoundaryBaseUrl
      .replace("{x}", (coords.x + 1).toString())
      .replace("{y}", (coords.y + 1).toString())
      .replace("{z}", (coords.z + 1).toString());

    return tileUrl;
  }
});

@Observer
@Component({
  components: {
    LoadingPanel,
    MapFiltersContainer,
    CollectionEventPopup,
    MapAddressSearch,
    TruckMarkers,
    VideoRequestMarkers,
    RequestPopup,
    FormattedAddress
  }
})
export default class Map extends Vue {
  minimumSearchResultZoomLevel = 17;

  mapStore = mapStore;
  userStore = userStore;
  loading = new LoadingStore();

  isTaskUnsuccessful = isTaskUnsuccessful;
  popup: L.Popup | null = null;

  dispose?: () => void;
  mapOptions = {
    zoomSnap: 0.5,
    attributionControl: false,
    zoomControl: false
  };
  clusterSettings = {
    disableClusteringAtZoom: 18
  };
  lgaLayerSettings = {
    format: "image/png",
    layers: "AUS_LGA",
    transparent: true,
    version: "1.3.0",
    styles: "LineStyleDash",
    crs: L.CRS.EPSG4326
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  propertyBoundaryLayer: L.TileLayer = new (L.TileLayer as any).PropertyBoundaries("", { maxZoom: mapStore.maxZoom, zIndex: 999 });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  aerialLayer: L.Layer | undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  streetLayer: L.Layer | undefined;

  councilBoundaryLayer = new L.TileLayer.WMS(mapStore.lgaBaseUrl, {
    layers: this.lgaLayerSettings.layers,
    transparent: this.lgaLayerSettings.transparent,
    format: this.lgaLayerSettings.format,
    version: this.lgaLayerSettings.version,
    styles: this.lgaLayerSettings.styles,
    crs: this.lgaLayerSettings.crs,
    zIndex: 99
  });

  newBinEvents: BinEvent[] = [];
  markerLayer: L.LayerGroup = new L.LayerGroup();
  indexMarkers: { index: string; marker: L.Marker }[] = [];
  requestLayer: L.LayerGroup = new L.LayerGroup();

  get selectedAddressIcon() {
    const icon = L.Icon.Default.extend({ options: { iconUrl: require("@/assets/icons/House Pin.svg"), shadowSize: [0, 0], iconSize: [60, 60], className: "house-icon" } });
    return new icon();
  }

  created() {
    if (!this.mapStore.userSelectedAddress) {
      mapStore.resetZoomAndSelection();
    }
  }

  mounted() {
    this.markerLayer.addTo(this.mapObject);
    this.requestLayer.addTo(this.mapObject);

    const displayCouncilBoundaryLayers = () => {
      if (mapStore.isLgaLayerVisible) {
        this.councilBoundaryLayer.addTo(this.mapObject);
      } else {
        this.councilBoundaryLayer.removeFrom(this.mapObject);
      }
    };

    const disposeWMSLayerChangedHandler = reaction(() => mapStore.isLgaLayerVisible, displayCouncilBoundaryLayers, { fireImmediately: true });

    const disposeCouncilConfigurationChangedHandler = reaction(() => userStore.councilConfiguration, this.onCouncilIsSatelliteViewChanged, { fireImmediately: true });

    const disposeLayerChangedHandler = reaction(() => mapStore.isSatelliteView, this.onCouncilIsSatelliteViewChanged);

    const disposeRequestChangeChangedHandler = reaction(() => mapStore.requests, this.onRequestsChanged);

    const disposeRequestVisibilityHandler = reaction(() => mapStore.isRequestsVisible, this.onRequestsVisibilityChanged);

    const disposeCollectionEventsChangedHandler = reaction(() => mapStore.collectionEvents, this.onCollectionEventsChanged);
    const disposeCollectionIndexesChangedHandler = reaction(() => mapStore.collectionIndexes, this.onCollectionIndexesChanged);
    const disposeCollectionEventsLengthChangedHandler = reaction(() => mapStore.collectionEvents.length || mapStore.collectionIndexes.length, this.processNewBinEvents, {
      delay: 5000
    });
    eventsHub.binEvent.subscribe(this.onBinEvent);

    const disposeWasteStreamConfigurationChangedHandler = reaction(() => toJS(mapStore.wasteConfiguration), this.onWasteConfigurationChanged);

    const disposeMaxBoundsChangeHandler = reaction(
      () => mapStore.maxBounds,
      maxBounds => {
        // Due to the way leaflet updates the map when the center AND max bounds change at the same time, we need to briefly remove the max
        // bounds so that the center can be set correctly

        if (maxBounds) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.mapObject.setMaxBounds(undefined as any);
          setTimeout(() => {
            this.mapObject.setMaxBounds(maxBounds);
          }, 1);
        }
      }
    );

    const disposeUserSelectedAddressPointInfoHandler = reaction(() => mapStore.userSelectedAddressGeocode, this.onUserSelectedAddressChanged);

    const disposeSelectedRequestChangedHandler = reaction(
      () => mapStore.selectedRequest,
      (request: RequestModel | null) => {
        if (!request && this.mapObject) {
          this.mapObject.closePopup();
        }
      }
    );

    const disposeUpdatePropertyBoundaryLayer = reaction(
      () => ({ isVisible: mapStore.isPropertyBoundaryLayerVisible, zoom: mapStore.zoom }),
      ({ isVisible, zoom }) => {
        if (isVisible && zoom >= mapStore.minPropertyBoundaryZoom && userStore.showSpectrumPropertyBoundaries()) {
          this.propertyBoundaryLayer.addTo(this.mapObject);
        } else {
          this.propertyBoundaryLayer.removeFrom(this.mapObject);
        }
      }
    );

    const disposeUserSelectedRequestHandler = reaction(
      () => ({ isOpen: mapStore.isRequestPopupOpen, request: mapStore.selectedRequest }),
      ({ isOpen, request }) => {
        if (request && isOpen) {
          setTimeout(() => {
            this.popup = L.popup({
              closeButton: false,
              className: "request-popup v-application",
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              autoPanPaddingTopLeft: (this as any).$mq === "sm" ? [5, 66] : [5, 80],
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              autoPanPaddingBottomRight: (this as any).$mq === "sm" ? [5, 5] : [320, 5],
              offset: [0, -50]
            })
              .setLatLng(latLng(request.latitude, request.longitude))
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .setContent((this.$refs.requestpopup as Vue).$el as HTMLElement)
              .openOn(this.mapObject)
              .on("remove", () => {
                if (mapStore.selectedRequest === request) {
                  mapStore.isRequestPopupOpen = false;
                  mapStore.selectedRequest = null;
                }
                this.popup = null;
              });
          }, 10);
        } else {
          this.mapObject.closePopup();
        }
      }
    );

    this.dispose = () => {
      disposeCollectionEventsChangedHandler();
      disposeCollectionIndexesChangedHandler();
      disposeWasteStreamConfigurationChangedHandler();
      disposeMaxBoundsChangeHandler();
      disposeUserSelectedAddressPointInfoHandler();
      disposeCouncilConfigurationChangedHandler();
      disposeLayerChangedHandler();
      disposeWMSLayerChangedHandler();
      disposeSelectedRequestChangedHandler();
      eventsHub.binEvent.unsubscribe(this.onBinEvent);
      disposeCollectionEventsLengthChangedHandler();
      disposeUpdatePropertyBoundaryLayer();
      disposeUserSelectedRequestHandler();
      disposeRequestChangeChangedHandler();
      disposeRequestVisibilityHandler();
    };

    if (this.mapStore.userSelectedAddress) {
      this.onUserSelectedAddressChanged();
    }

    this.onRequestsVisibilityChanged();
  }

  onCollectionEventsChanged() {
    this.drawCollectionEvents();
    addDataCyToCollectionEventIcons(this.mapStore.collectionEvents);
  }

  onRequestsChanged() {
    this.requestLayer.clearLayers();

    // Filter out cancelled requests
    const visibleRequests = this.mapStore.requests.filter((request: RequestModel) => request.status && request.status.toUpperCase() !== "CANCELLED");

    console.debug("Requests changed", visibleRequests.length);

    visibleRequests.forEach((request: RequestModel) => {
      const marker = L.marker(latLng(request.latitude, request.longitude), {
        icon: this.requestIcon(request),
        alt: "request-" + request.requestId
      });
      marker.on("click", () => {
        mapStore.fetchRequestDetails(request);
        mapStore.isRequestPopupOpen = true;
      });
      this.requestLayer.addLayer(marker);
    });
  }

  onRequestsVisibilityChanged() {
    if (this.mapStore.isRequestsVisible) {
      this.requestLayer.addTo(this.mapObject);
    } else {
      this.requestLayer.removeFrom(this.mapObject);
    }
  }

  requestIcon(request: RequestModel) {
    const requestIdentifier = (request.addressUnitNo ? request.addressUnitNo + "-" : "") + request.addressStreetNo + "-" + request.addressStreetName.replace(/ /g, "-");
    const icon = new L.DivIcon({
      html: requestMarker(getStatus(request), unsuccessfulTaskCount(request), requestIdentifier),
      shadowSize: [0, 0],
      iconSize: [60, 60],
      className: "truck-icon",
      iconAnchor: [30, 60]
    });
    return icon;
  }

  onCouncilIsSatelliteViewChanged() {
    this.aerialLayer =
      userStore.councilConfiguration?.mapProvider == MapProvider.Old
        ? (L as any).bingLayer(process.env.VUE_APP_MAP_TILES_API_KEY, { imagerySet: "AerialWithLabelsOnDemand" })
        : L.tileLayer(`${process.env.VUE_APP_HERE_MAP_BASE_URL}?apiKey=${process.env.VUE_APP_HERE_MAP_TILES_API_KEY}&style=logistics.satellite.day`, {
            maxZoom: mapStore.maxZoom,
            minZoom: mapStore.minZoom,
            attribution: "&copy;2024 HERE Technologies"
          });

    this.streetLayer =
      userStore.councilConfiguration?.mapProvider == MapProvider.Old
        ? (L as any).bingLayer(process.env.VUE_APP_MAP_TILES_API_KEY, { imagerySet: "RoadOnDemand" })
        : L.tileLayer(`${process.env.VUE_APP_HERE_MAP_BASE_URL}?apiKey=${process.env.VUE_APP_HERE_MAP_TILES_API_KEY}`, {
            maxZoom: mapStore.maxZoom,
            minZoom: mapStore.minZoom,
            attribution: "&copy;2024 HERE Technologies"
          });

    if (!this.aerialLayer || !this.streetLayer) {
      return;
    }

    if (mapStore.isSatelliteView) {
      this.aerialLayer.addTo(this.mapObject);
      this.streetLayer.removeFrom(this.mapObject);
    } else {
      this.aerialLayer.removeFrom(this.mapObject);
      this.streetLayer.addTo(this.mapObject);
    }
  }

  drawCollectionEvents() {
    this.markerLayer.clearLayers();

    for (let i = 0; i < this.mapStore.collectionEvents.length; i++) {
      const collectionEvent = this.mapStore.collectionEvents[i];
      if (mapStore.isEventVisible(collectionEvent)) {
        const marker = this.createCollectionEventMarker(collectionEvent);
        this.markerLayer.addLayer(marker);
      }
    }
  }

  onCollectionIndexesChanged() {
    this.drawCollectionIndexes();
  }

  drawCollectionIndexes() {
    this.markerLayer.clearLayers();

    for (let i = 0; i < mapStore.collectionIndexes.length; i++) {
      const model = mapStore.collectionIndexes[i];
      const marker = this.createCollectionIndexMarker(model);
      if (marker) {
        this.markerLayer.addLayer(marker);
        this.indexMarkers.push({ index: model.index, marker });
      }
    }
  }

  createCollectionIndexMarker(e: CollectionIndexModel): L.Marker | null {
    const wasteStreamStats = e.wasteStreams.map(c => {
      let total = 0;

      const wasteConfig = mapStore.wasteConfiguration.find(w => w.id === c.wasteStreamId);
      if (!wasteConfig) return { id: c.wasteStreamId, total };

      if (wasteConfig.isCollectionsVisible) {
        total += c.collections;
      }

      if (wasteConfig.isIssuesVisible) {
        total += c.collectionIssues;
      }

      return { id: c.wasteStreamId, total };
    });

    const stats = wasteStreamStats.reduce((a, c) => {
      a[c.id] = c.total;
      return a;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }, {} as any);

    const population = wasteStreamStats.reduce((a, c) => a + c.total, 0);

    if (population <= 0) {
      return null;
    }

    const pi2 = Math.PI * 2;

    const markerClusterIcon = L.Icon.extend({
      options: {
        iconSize: new L.Point(44, 44),
        className: "prunecluster leaflet-markercluster-icon"
      },

      createIcon: function() {
        // based on L.Icon.Canvas from shramov/leaflet-plugins (BSD licence)
        const e = document.createElement("canvas");
        this._setIconStyles(e, "icon");
        const s = this.options.iconSize;
        e.width = s.x;
        e.height = s.y;
        this.draw(e.getContext("2d"), s.x, s.y);
        return e;
      },

      createShadow: function() {
        return null;
      },

      draw: function(canvas: CanvasRenderingContext2D) {
        let start = 0;
        forOwn(mapStore.collectionEventColours, (value, key) => {
          const size = this.stats[key] / this.population;

          if (size > 0) {
            canvas.beginPath();
            canvas.moveTo(22, 22);
            canvas.fillStyle = value;
            let from = start + 0.14;
            const to = start + size * pi2;

            if (to < from) {
              from = start;
            }
            canvas.arc(22, 22, 22, from, to);

            start = start + size * pi2;
            canvas.lineTo(22, 22);
            canvas.fill();
            canvas.closePath();
          }
        });

        canvas.beginPath();
        canvas.fillStyle = "white";
        canvas.arc(22, 22, 18, 0, Math.PI * 2);
        canvas.fill();
        canvas.closePath();

        canvas.fillStyle = "#555";
        canvas.textAlign = "center";
        canvas.textBaseline = "middle";
        canvas.font = "bold 12px sans-serif";
        canvas.canvas.setAttribute("data-cy", "cluster-" + this.population);

        canvas.fillText(this.population, 22, 22, 40);
      }
    });

    const onClusterClick = (latLng: LatLng) => () => {
      this.mapObject.flyTo(latLng, Math.min(mapStore.zoom + 3, 18));
    };

    const icon = new markerClusterIcon();
    icon.stats = stats;
    icon.population = population;

    const marker = new L.Marker(latLng(e.latitude, e.longitude), { icon });
    marker.on("click", onClusterClick(latLng(e.latitude, e.longitude)));

    return marker;
  }

  onBinEvent(payload: BinEvent) {
    this.newBinEvents.push(payload);
    console.debug("Unprocessed BinEvents", payload);
  }

  processNewBinEvents() {
    if (mapStore.isToday === false) {
      this.newBinEvents = [];
      return;
    }

    if (mapStore.zoom < this.clusterSettings.disableClusteringAtZoom) {
      this.newBinEvents = [];
      return;
    }
    const start = +new Date();

    for (const payload of this.newBinEvents) {
      try {
        if (userStore.activeCouncil && userStore.activeCouncil === payload.council) {
          const model: CollectionEventSummaryModel = {
            id: payload.id,
            latitude: payload.latitude,
            longitude: payload.longitude,
            wasteStreamId: payload.wasteStreamId,
            isCollectionIssue: payload.isCollectionIssue === 1,
            collectionEventType: payload.collectionEventType,
            addressHash: payload.addressHash
          };
          const marker = this.createCollectionEventMarker(model);
          this.markerLayer.addLayer(marker);
        } else {
          console.debug("Skipping NewBinEvent due to council mismatch", userStore.activeCouncil, payload.council);
          continue;
        }
      } catch (e) {
        console.error("Failed to process NewBinEvent", payload, e);
      }
    }

    console.debug(`Processed ${this.newBinEvents.length} new bin events in ${+new Date() - start}ms`);
    this.newBinEvents = [];
  }

  private createCollectionEventMarker(e: CollectionEventSummaryModel) {
    const onMarkerClick = (e: CollectionEventSummaryModel) => () => {
      mapStore.selectedCollectionEvent = e;
      mapStore.isCollectionEventPopupOpen = true;
    };

    if (e.isCollectionIssue) {
      const marker = L.marker(latLng(e.latitude, e.longitude), {
        icon: mapStore.collectionIssueIcons[e.wasteStreamId],
        alt: "issue-" + e.wasteStreamId + "-" + e.id
      });
      marker.on("click", onMarkerClick(e));
      return marker;
    } else {
      const marker = L.marker(latLng(e.latitude, e.longitude), {
        icon: mapStore.collectionEventIcons[e.wasteStreamId],
        alt: "collection-" + e.wasteStreamId + "-" + e.id
      });
      marker.on("click", onMarkerClick(e));
      return marker;
    }
  }

  onWasteConfigurationChanged() {
    if (mapStore.zoom < this.clusterSettings.disableClusteringAtZoom) {
      this.drawCollectionIndexes();
    } else {
      this.drawCollectionEvents();
    }
  }

  onUserSelectedAddressChanged() {
    this.mapObject.closePopup();
    if (!this.mapStore.userSelectedAddressGeocode) {
      return;
    }

    const zoomLevel = Math.max(this.minimumSearchResultZoomLevel, mapStore.zoom);
    this.mapObject.flyTo([this.mapStore.userSelectedAddressGeocode.latitude, this.mapStore.userSelectedAddressGeocode.longitude], zoomLevel);
  }

  destroyed() {
    if (this.dispose) this.dispose();
  }

  onZoomUpdated(zoom: number) {
    console.debug("Map zoom: " + zoom);
    mapStore.zoom = zoom;
  }

  onCenterUpdated(center: LatLng) {
    console.debug("Map center:" + JSON.stringify(center));
    mapStore.center = center;
  }

  async onBoundsUpdated(bounds: LatLngBounds) {
    console.debug("Map bounds:" + JSON.stringify(bounds));
    mapStore.bounds = bounds;

    await this.fetchCollectionEvents(mapStore.zoom);
  }

  async fetchCollectionEvents(zoom: number) {
    this.markerLayer.clearLayers();
    if (zoom >= this.clusterSettings.disableClusteringAtZoom) {
      await mapStore.fetchCollectionEvents();
    } else {
      await mapStore.fetchCollectionIndexes();
    }
  }

  get mapObject() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.$refs.map as any).mapObject as LMap;
  }

  get lastCollectionEventDate() {
    return mapStore.userSelectedAddressGeocode?.lastCollectionEventDate ? format(new Date(mapStore.userSelectedAddressGeocode.lastCollectionEventDate), DATE_FORMAT) : "Unknown";
  }

  get canChangeCollectionDate() {
    if (!mapStore.userSelectedAddressGeocode?.lastCollectionEventDate) {
      return false;
    }

    return !isSameDay(new Date(mapStore.date), new Date(mapStore.userSelectedAddressGeocode.lastCollectionEventDate));
  }

  updateSelectedDate() {
    if (mapStore.userSelectedAddressGeocode?.lastCollectionEventDate) {
      mapStore.date = format(new Date(mapStore.userSelectedAddressGeocode.lastCollectionEventDate), "yyyy-MM-dd");
    }
  }
}
